// hooks/useDebounce.ts

import { useState, useEffect } from "react";

/**
 * Hook para debouncing de valores.
 * Útil para búsquedas en tiempo real sin hacer requests en cada tecla.
 *
 * @param value - El valor a debounce
 * @param delay - Delay en milisegundos (default: 500ms)
 * @returns El valor debounced
 *
 * @example
 * ```tsx
 * const [search, setSearch] = useState("");
 * const debouncedSearch = useDebounce(search, 300);
 *
 * // debouncedSearch se actualiza solo 300ms después de que el usuario para de escribir
 * ```
 */
export function useDebounce<T>(value: T, delay: number = 500): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    // Set timeout para actualizar el valor debounced
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    // Cleanup: cancelar timeout si el valor cambia antes del delay
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Variante que también devuelve un flag de "isDebouncing"
 * Útil para mostrar indicador de loading mientras se espera
 *
 * @example
 * ```tsx
 * const [search, setSearch] = useState("");
 * const { debouncedValue, isDebouncing } = useDebouncedValue(search, 300);
 *
 * return (
 *   <div>
 *     <input value={search} onChange={(e) => setSearch(e.target.value)} />
 *     {isDebouncing && <span>Buscando...</span>}
 *   </div>
 * );
 * ```
 */
export function useDebouncedValue<T>(value: T, delay: number = 500) {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  const [isDebouncing, setIsDebouncing] = useState(false);

  useEffect(() => {
    setIsDebouncing(true);

    const handler = setTimeout(() => {
      setDebouncedValue(value);
      setIsDebouncing(false);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return { debouncedValue, isDebouncing };
}

/**
 * Hook para debounce de callbacks/funciones
 * Útil cuando quieres ejecutar una función después de un delay
 *
 * @example
 * ```tsx
 * const saveData = useDebouncedCallback(
 *   async (data) => {
 *     await api.save(data);
 *   },
 *   1000
 * );
 *
 * // Se ejecuta solo 1 segundo después de la última llamada
 * <button onClick={() => saveData(formData)}>Save</button>
 * ```
 */
export function useDebouncedCallback<T extends (...args: any[]) => any>(
  callback: T,
  delay: number = 500,
): (...args: Parameters<T>) => void {
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Cleanup al desmontar
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [timeoutId]);

  return (...args: Parameters<T>) => {
    // Cancelar timeout anterior si existe
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    // Crear nuevo timeout
    const newTimeoutId = setTimeout(() => {
      callback(...args);
    }, delay);

    setTimeoutId(newTimeoutId);
  };
}

/**
 * Hook con control manual de debouncing
 * Te permite cancelar o forzar la ejecución
 *
 * @example
 * ```tsx
 * const {
 *   debouncedValue,
 *   isDebouncing,
 *   cancel,
 *   flush
 * } = useControllableDebounce(search, 300);
 *
 * return (
 *   <div>
 *     <input value={search} onChange={handleChange} />
 *     {isDebouncing && <Spinner />}
 *     <button onClick={cancel}>Cancelar</button>
 *     <button onClick={flush}>Buscar Ahora</button>
 *   </div>
 * );
 * ```
 */
export function useControllableDebounce<T>(value: T, delay: number = 500) {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  const [isDebouncing, setIsDebouncing] = useState(false);
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setIsDebouncing(true);

    const handler = setTimeout(() => {
      setDebouncedValue(value);
      setIsDebouncing(false);
      setTimeoutId(null);
    }, delay);

    setTimeoutId(handler);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  // Cancelar el debounce
  const cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      setIsDebouncing(false);
      setTimeoutId(null);
    }
  };

  // Forzar la actualización inmediata
  const flush = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      setDebouncedValue(value);
      setIsDebouncing(false);
      setTimeoutId(null);
    }
  };

  return {
    debouncedValue,
    isDebouncing,
    cancel,
    flush,
  };
}
